import * as React from 'react';
import { Router } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@common-fe/common-fe';
import { Box, FormProvider, ThemeWrapper as CommonThemeWrapper, useForm } from '@common-fe/common-fe';
import { render } from '@testing-library/react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Grommet } from 'grommet';
import { createMemoryHistory } from 'history';

import { elevate } from '@/styles/grommet-theme';
import theme from '@/styles/theme';

dayjs.extend(relativeTime);

export const testingHistory = createMemoryHistory();

export const testingCombinedTheme = {
  ...elevate,
  ...theme,
};

export const testingQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});
interface Props {
  children?: React.ReactNode;
}
export const DefaultTestContainer: React.FC<Props> = ({ children }) => (
  <Router history={testingHistory}>
    <QueryClientProvider client={testingQueryClient}>
      <CommonThemeWrapper>
        <Grommet theme={testingCombinedTheme}>
          <Box data-testid="outside-test">{children}</Box>
        </Grommet>
      </CommonThemeWrapper>
    </QueryClientProvider>
  </Router>
);

const TestRouterWrapper: React.FC<Props> = ({ children }) => (
  <Router history={testingHistory}>{children}</Router>
);
const TestQueryClientWrapper: React.FC<Props> = ({ children }) => (
  <QueryClientProvider client={testingQueryClient}>{children}</QueryClientProvider>
);
export const HookWrapper: React.FC<Props> = ({ children }) => (
  <TestRouterWrapper>
    <TestQueryClientWrapper>{children}</TestQueryClientWrapper>
  </TestRouterWrapper>
);

export const getWrapper: () => React.FC<Props> =
  () =>
  ({ children }) =>
    <DefaultTestContainer>{children}</DefaultTestContainer>;

const renderTheme = (element: React.ReactElement, options?: object) =>
  render(element, {
    wrapper: DefaultTestContainer,
    ...options,
  });

interface RenderOptions {
  withRouter?: boolean;
  withForm?: boolean;
  withQuery?: boolean;
}
export const getWrapperWithOptions =
  (options?: RenderOptions) =>
  ({ children }: { children: React.ReactNode }) => {
    let currentNode = (
      <CommonThemeWrapper>
        <Grommet theme={testingCombinedTheme}>
          <Box data-testid="outside-test">{children}</Box>
        </Grommet>
      </CommonThemeWrapper>
    );
    if (options?.withQuery) {
      currentNode = <QueryClientProvider client={testingQueryClient}>{currentNode}</QueryClientProvider>;
    }
    if (options?.withForm) {
      const formContext = useForm();
      currentNode = <FormProvider {...formContext}>{currentNode}</FormProvider>;
    }
    if (options?.withRouter) {
      currentNode = <Router history={testingHistory}>{currentNode}</Router>;
    }
    return currentNode;
  };

export const getHookTestWrapper =
  (options?: RenderOptions) =>
  ({ children }: { children: React.ReactNode }) => {
    let currentNode: React.ReactNode = children;
    if (options?.withQuery) {
      currentNode = <QueryClientProvider client={testingQueryClient}>{currentNode}</QueryClientProvider>;
    }
    if (options?.withForm) {
      const formContext = useForm();
      currentNode = <FormProvider {...formContext}>{currentNode}</FormProvider>;
    }
    if (options?.withRouter) {
      currentNode = <Router history={testingHistory}>{currentNode}</Router>;
    }
    return currentNode;
  };

export const renderThemeWithOptions = (
  element: React.ReactElement,
  renderOptions?: RenderOptions,
  options?: object
) =>
  render(element, {
    wrapper: getWrapperWithOptions(renderOptions),
    ...options,
  });
export default renderTheme;
